import React, { useCallback, useEffect, useState } from 'react'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { Dialog } from '@/components/ui/dialog'
import { SortDescriptor } from '@progress/kendo-data-query'
import { GridColumn as Column, Grid, GridSortChangeEvent } from '@progress/kendo-react-grid'
import { PageData } from 'api/Pagination/Types'
import {
  WelcomeTradingAccountDto,
  WelcomeTradingAccountFilterKey,
} from 'api/Trading-Accounts/Types/WelcomeTradingAccountDto'
import { Pager } from 'components/components/Pager'
import { FilterType } from 'components/Filter'
import { SelectClientModalOpenEvent } from 'components/Filter/ClientFields/SelectClientFilter'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import { SelectLeadFilter } from 'components/Filter/LeadFields/SelectLeadFilter'
import { getTradingAccountWelcomeFilterFields } from 'components/Filter/tradingAccountsWelcomeFields'
import { Spinner } from 'components/Spinner'
import { createGridState, useGridFilter, useGridState, useGridStatusState } from 'features/DataGrid'
import { isGoBack } from 'lib/react-router-dom'
import { useAppDispatch, useAppSelector } from 'store'
import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  GridFilterActionState,
  resetGridFilters,
  selectGridFilterByFilterId,
} from 'store/grid'
import { fetchTickmillCompaniesAction } from 'store/identity/identity.actions'
import {
  fetchTradingAccountStatusAction,
  fetchWelcomeTradingAccountGroupsAction,
} from 'store/trading-accounts/trading-accounts.actions'
import { GridFilterOperatorEnum, GridFilterOperatorNameEnum } from 'types/GridFilterOperatorEnum'
import { useApiClient } from 'utils/api/ApiClient'
import { IdentityApiClient } from 'utils/api/identityApi'
import { LeadsApiClient } from 'utils/api/leadsApi'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { buildQuery, buildSortQuery } from 'utils/QueryHelpers'
import { TableWithExport } from 'utils/table/withExport'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import { CustomColumnMenu } from '../../Clients/CustomColumnMenu'
import SelectLeadTable, { SelectLeadEvent } from '../../Leads/SelectLeadTable'
import { normalizeWelcomeTradingAccountsSearchQuery } from './normalizer'
import { GridColumn, welcomeTradingAccountColumns } from './welcomeTradingAccountColumns'

const useMapStateToProps = () => ({
  tradingAccountGroup: useAppSelector((state) => state.tradingAccounts.tradingAccountWelcomeGroup),
  tickmillCompanies: useAppSelector((state) => state.identity.tickmillCompanies),
  tradingAccountStatus: useAppSelector((state) => state.tradingAccounts.tradingAccountStatus),
  gridState: useAppSelector((state) => state.grid),
  gridFilterByFilterIdSelector: useAppSelector(selectGridFilterByFilterId),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const identityApiClient = useApiClient(IdentityApiClient)
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  return {
    fetchTradingAccountStatus: async () =>
      dispatch(fetchTradingAccountStatusAction(tradingAccountsApiClient)),
    fetchTradingAccountGroups: async () =>
      dispatch(fetchWelcomeTradingAccountGroupsAction(tradingAccountsApiClient)),
    fetchTickmillCompanies: async () => dispatch(fetchTickmillCompaniesAction(identityApiClient)),
    resetGridFilters: async () => dispatch(resetGridFilters()),
  }
}

export const WelcomeTradingAccounts = (): JSX.Element => {
  const crumbs = [{ title: 'Trading Accounts - Welcome', link: links.recordsWelcomeAccounts.url }]

  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })

  const leadsApiClient = useApiClient(LeadsApiClient)

  const [tradingAccounts, setTradingAccounts] = useState<WelcomeTradingAccountDto[]>([])
  const [columns, setColumns] = useState<GridColumn[]>([])

  const [isLeadModalOpen, setLeadModalOpen] = useState<boolean>(false)
  const [leadModalFilterId, setLeadModalFilterId] = useState<string>()

  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { gridState, gridActions } = useGridState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)
  const {
    tradingAccountGroup,
    tickmillCompanies,
    tradingAccountStatus,
    gridFilterByFilterIdSelector,
  } = useMapStateToProps()

  const { fetchTradingAccountStatus, fetchTradingAccountGroups, fetchTickmillCompanies } =
    useMapDispatchToProps()

  useEffect(() => {
    ;(async () => {
      await fetchGrid(
        async (
          gridFilterState: GridFilterActionState
        ): Promise<PageData<WelcomeTradingAccountDto[]>> => {
          const searchQuery = getQuery(gridFilterState.filter)
          const sortQuery = getSort(gridFilterState.sort)
          const response = await leadsApiClient.getLeadsWelcomeAccounts({
            search: searchQuery,
            sort: sortQuery,
            pageIndex: gridFilterState.pageIndex,
            pageSize: gridFilterState.pageSize,
            skipCount: tableData?.skipCount !== false,
          })

          if (isEmpty(tradingAccountGroup)) await fetchTradingAccountGroups()
          if (isEmpty(tickmillCompanies)) await fetchTickmillCompanies()
          if (isEmpty(tradingAccountStatus)) await fetchTradingAccountStatus()

          setColumns(welcomeTradingAccountColumns)
          if (response?.items) {
            setTradingAccounts(response.items)
          }
          setTableData({ ...tableData, meta: response })
          return response
        }
      )
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGrid = async (
    callback: (
      gridFilterState: GridFilterActionState
    ) => Promise<PageData<WelcomeTradingAccountDto[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const gridFilterState = selectFilterForGoBackHistory(
      createGridState({ skip: 0 }, gridState, { isGoBack })
    )
    const response = await callback(gridFilterState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      return (
        buildQuery(normalizeWelcomeTradingAccountsSearchQuery(filter ?? gridState.filter)) ?? null
      )
    },
    [gridState.filter]
  )

  const getSort = useCallback(
    (sortDescriptors?: SortDescriptor[]): string | null => {
      return buildSortQuery(sortDescriptors ?? gridState.sort)
    },
    [gridState.sort]
  )

  const filterFetch = useCallback(
    async (params?: { query?: string | null }): Promise<void> => {
      const searchQuery = params?.query !== undefined ? params.query : getQuery()
      const sortQuery = getSort(gridState.sort)
      const pageIndex = 1
      const pageSize = gridState.pageSize ?? 10
      const take = gridState.take ?? 10
      const skip = 0

      const response = await leadsApiClient.getLeadsWelcomeAccounts({
        search: searchQuery,
        sort: sortQuery,
        pageIndex,
        pageSize,
        skipCount: tableData?.skipCount !== false,
      })
      const total = response.totalResults

      if (response?.items) {
        setTradingAccounts(response.items)
      }
      gridActions.setGridFilters({
        take,
        skip,
        total,
        pageIndex,
      })
      setTableData({ ...tableData, meta: response })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      getQuery,
      getSort,
      gridActions,
      gridState.pageSize,
      gridState.sort,
      gridState.take,
      tableData?.skipCount,
    ]
  )

  const handleLeadModalOpen = (event: SelectClientModalOpenEvent) => {
    setLeadModalOpen(true)
    setLeadModalFilterId(event.filterId)
  }

  const handleLeadModalClose = () => {
    setLeadModalOpen(false)
  }

  const handleSelectedLeads = (event: SelectLeadEvent) => {
    const filter = event?.filterId ? gridFilterByFilterIdSelector(event.filterId) : null
    if (filter) {
      gridActions.addGridFiltersByFilterId({
        ...filter,
        id: event.filterId,
        value: event.value,
      })
    }
  }

  const handlePageSizeChange = async (pageSize: number) => {
    await gridActions.setGridFilters({
      ...gridState,
      pageSize,
    })

    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await leadsApiClient.getLeadsWelcomeAccounts({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: 1,
      pageSize,
      skipCount: tableData?.skipCount !== false,
    })
    await gridActions.setGridFilters({
      ...gridState,
      pageIndex: 1,
      take: pageSize,
      total: response.totalResults,
    })
    if (response?.items) {
      setTradingAccounts(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleSortChange = async (event: GridSortChangeEvent) => {
    gridActions.setGridFilters({
      sort: event.sort,
    })

    const searchQuery = getQuery(gridState.filter)
    const sortQuery = getSort(event.sort)
    const response = await leadsApiClient.getLeadsWelcomeAccounts({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.pageSize,
      skipCount: tableData?.skipCount !== false,
    })

    if (response?.items) {
      setTradingAccounts(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleSkipCountChange = async (skipCount: boolean) => {
    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await leadsApiClient.getLeadsWelcomeAccounts({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.take,
      skipCount,
    })
    const total = response.totalResults

    if (response?.items) {
      setTradingAccounts(response.items)
    }
    gridActions.setGridFilters({
      total,
    })
    setTableData({ ...tableData, skipCount, meta: response })
  }

  const handleGridPageChange = async (page: number) => {
    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)
    const pageIndex = page
    gridActions.setGridFilters({
      pageIndex,
    })
    const response = await leadsApiClient.getLeadsWelcomeAccounts({
      search: searchQuery,
      sort: sortQuery,
      pageIndex,
      pageSize: gridState.take,
      skipCount: tableData?.skipCount !== false,
    })
    const total = response.totalResults

    gridActions.setGridFilters({
      total,
    })
    if (response?.items) {
      setTradingAccounts(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleColumnsSubmit = (columnsState: GridColumn[]) => {
    setColumns(columnsState)
  }

  if (gridStatusState.isLoading) {
    return <Spinner />
  }

  return (
    <React.Fragment>
      {isLeadModalOpen && (
        <Dialog open={isLeadModalOpen} onOpenChange={(open) => !open && handleLeadModalClose()}>
          <SelectLeadTable
            filterId={leadModalFilterId}
            onSelect={handleSelectedLeads}
            onClose={handleLeadModalClose}
          />
        </Dialog>
      )}
      <Breadcrumbs crumbs={crumbs} />
      <SelectFiltersFacade
        gridState={gridState}
        gridStatusActions={gridStatusActions}
        gridStatusState={gridStatusState}
        setGridFilters={gridActions.setGridFilters}
        filterFetch={filterFetch}
        filterType={FilterType.WELCOME_TRADING_ACCOUNT}
        filterFields={[
          ...handleFilters(handleLeadModalOpen),
          ...getTradingAccountWelcomeFilterFields(),
        ]}
        containerClassName='wallet-table__filter'
        getQuery={getQuery}
      />
      <TableStateContext.Provider value={[tableData, setTableData]}>
        <TableWithExport data={tradingAccounts}>
          <Grid
            {...gridState}
            resizable
            data={tradingAccounts}
            sort={gridState.sort}
            total={gridState.total}
            take={gridState.take}
            skip={gridState.skip}
            onSortChange={handleSortChange}
            pageable={true}
            pager={() => (
              <Pager
                onSkipCountChange={handleSkipCountChange}
                onPageChange={handleGridPageChange}
                onPageSizeChange={handlePageSizeChange}
                page={gridState.pageIndex}
                pageSize={gridState.pageSize}
                take={gridState.take || 10}
                skip={gridState.skip || 0}
              />
            )}
            style={{ zIndex: 0 }}
          >
            {columns
              .filter((c) => c.show)
              .map((column, idx) => (
                <Column
                  key={idx + 2}
                  columnMenu={(gridProps) => (
                    <CustomColumnMenu
                      {...gridProps}
                      columns={columns}
                      onColumnsSubmit={handleColumnsSubmit}
                    />
                  )}
                  {...column}
                />
              ))}
          </Grid>
        </TableWithExport>
      </TableStateContext.Provider>
    </React.Fragment>
  )
}

const handleFilters = (handleLeadModalOpen: (event: SelectClientModalOpenEvent) => void) => [
  {
    name: WelcomeTradingAccountFilterKey.lead,
    label: 'Lead',
    filter: (leadFilter: {
      filter: FilterDescriptor
      onFilterChange: (event: { nextFilter: FilterDescriptor }) => void
    }) => <SelectLeadFilter {...leadFilter} onLeadModalOpen={handleLeadModalOpen} />,
    operators: [
      {
        text: GridFilterOperatorNameEnum.equalOperator,
        operator: GridFilterOperatorEnum.equalOperator,
      },
      {
        text: GridFilterOperatorNameEnum.notEqOperator,
        operator: GridFilterOperatorEnum.notEqOperator,
      },
    ],
  },
]
